import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { get, type AdminUser, type AuditEntry } from '../api'
import { Panel } from '../components/ui'

const label: Record<string, string> = {
  'user.registered': 'registered',
  'stream_key.created': 'created a stream key',
  'stream_key.rotated': 'replaced the stream key',
  'stream_key.revoked': 'revoked the stream key',
  'destination.created': 'added a destination',
  'destination.updated': 'edited a destination',
  'destination.enabled': 'switched on',
  'destination.disabled': 'switched off',
  'destination.deleted': 'deleted a destination',
  'admin.user_blocked': 'blocked a user',
  'admin.user_unblocked': 'unblocked a user',
  'admin.stream_kicked': 'stopped a stream',
  'admin.stream_unsuspended': 'lifted a suspension',
  'admin.output_stopped': 'stopped an output',
  'admin.invite_created': 'created an invite',
  'ingest.denied': 'stream refused',
  'ingest.auth_unavailable': 'stream refused, API unavailable',
  'ingest.ip_banned': 'address banned',
  'stream.started': 'stream started',
  'stream.stopped': 'stream ended',
  'stream.takeover': 'encoder replaced',
  'output.state': 'output changed state',
  'admin.fill_platform': 'changed the default output bitrate',
  'admin.fill_profile': 'changed a profile’s output bitrate',
  'admin.output_fill': 'overrode a destination’s bitrate',
  'admin.user_features': 'changed console features',
}

const FILL_ACTIONS = new Set(['admin.fill_platform', 'admin.fill_profile', 'admin.output_fill', 'admin.user_features'])

/** One value of a fill change for people: null = inherited, thresholds as a short table. */
function fillValue(k: string, v: unknown): string {
  if (v === null) return k === 'fill_mode' || k === 'fill_quality' ? "profile's default" : "platform's default"
  if (v === undefined) return '—'
  if (typeof v === 'boolean') return v ? 'on' : 'off'
  if (k === 'margin_pct') return `${String(v)}%`
  if (k === 'thresholds' && typeof v === 'object') {
    return Object.entries(v as Record<string, number>).map(([c, n]) => `${c} ${n}`).join(', ') || 'none'
  }
  if (k === 'effective' && typeof v === 'object') {
    const e = v as { mode?: string; quality?: string }
    return e.mode === 'floor' ? `floor ${e.quality}` : String(e.mode)
  }
  return String(v)
}

/**
 * Fill changes (docs/bitrate-fill.md §4) carry the new values and `previous`: shown as
 * "mode: source → floor". admin.output_fill has no previous; its effective value is shown.
 */
function fillDetails(e: AuditEntry): string {
  const d = e.details as Record<string, unknown>
  const prev = (d.previous ?? {}) as Record<string, unknown>
  const parts: string[] = []
  for (const [k, v] of Object.entries(d)) {
    if (k === 'previous' || k === 'output_id') continue
    const label = k === 'effective' ? 'in force' : k.replace(/^fill_/, '').replace(/_/g, ' ')
    const now = fillValue(k, v)
    if (k in prev && fillValue(k, prev[k]) !== now) parts.push(`${label}: ${fillValue(k, prev[k])} → ${now}`)
    else if (!(k in prev)) parts.push(`${label}: ${now}`)
  }
  const target = e.action === 'admin.user_features' ? undefined : e.target
  return [target, d.output_id as string | undefined, ...parts].filter(Boolean).join(', ') || 'no change'
}

function details(e: AuditEntry) {
  const d = e.details as Record<string, string | undefined>
  if (FILL_ACTIONS.has(e.action)) return fillDetails(e)
  if (e.action === 'output.state') return `${d.name ?? e.target}: ${d.from} → ${d.to}${d.reason ? ` (${d.reason})` : ''}`
  const parts = Object.entries(d)
    // a fill choice of null (back to the default) is a value too (destination.created / updated)
    .filter(([k, v]) => v !== undefined && v !== '' && (typeof v !== 'object' || (v === null && k.startsWith('fill_'))))
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v === null ? 'default' : v}`)
  return [e.target, ...parts].filter(Boolean).join(', ')
}

export function AdminAudit() {
  const [user, setUser] = useState(0)
  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => get<AdminUser[]>('/api/admin/users') })
  const log = useQuery({
    queryKey: ['admin-audit', user],
    queryFn: () => get<AuditEntry[]>(`/api/admin/audit?limit=200${user ? `&user_id=${user}` : ''}`),
    refetchInterval: 5000,
  })
  return (
    <Panel
      title="Log"
      aside={
        <select
          aria-label="Filter by user"
          value={user}
          onChange={(e) => setUser(Number(e.target.value))}
          className="rounded-lg border border-line bg-panel px-2 py-1 text-sm"
        >
          <option value={0}>Everyone</option>
          {users.data?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.email}
            </option>
          ))}
        </select>
      }
    >
      <ul className="-mx-4 -my-4 divide-y divide-line text-sm">
        {log.data?.map((e) => (
          <li key={e.id} data-testid={`audit-${e.id}`} data-action={e.action} className="grid gap-x-4 px-4 py-2 sm:grid-cols-[10rem_minmax(0,1fr)]">
            <time className="num text-xs text-muted" dateTime={e.created_at}>
              {new Date(e.created_at).toLocaleString()}
            </time>
            <p>
              <span className="font-medium">{e.actor ?? (e.action.startsWith('ingest') || e.action.startsWith('stream') || e.action.startsWith('output') ? 'Media server' : 'System')}</span>{' '}
              {label[e.action] ?? e.action}
              {e.user && e.user !== e.actor && <span className="text-muted"> for {e.user}</span>}
              <span className="block text-xs text-muted" data-testid={`audit-details-${e.id}`}>{details(e)}</span>
            </p>
          </li>
        ))}
        {log.data?.length === 0 && <li className="px-4 py-3 text-muted">Nothing logged yet.</li>}
      </ul>
    </Panel>
  )
}
